import { format, isToday, isTomorrow } from "date-fns";
import { CalendarX, CheckSquare } from "react-bootstrap-icons";
import { cn } from "@utils";
import { itemAnchorDate, projectionIdentity } from "../common";

const dayLabel = (date) => {
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  return format(date, "EEE d MMM");
};

// A flat, chronological list — no day grouping and no status badges. Each row shows the same
// content as the Month and Week views (type label, instalment position, account code), laid out
// over two lines: the title, with the account code on the line below.
const AgendaList = ({ items, onSelectItem }) => {
  const sorted = [...items]
    .filter((item) => itemAnchorDate(item))
    .sort((a, b) => itemAnchorDate(a) - itemAnchorDate(b));

  if (sorted.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white py-16 text-zinc-400 shadow-sm">
        <CalendarX className="size-8" />
        <p className="text-sm">Nothing scheduled in this period</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-sm">
      <ul className="divide-y divide-zinc-100">
        {sorted.map((item) => {
          const identity = projectionIdentity(item);
          const isTask = !identity && item.kind === "task";
          const { reference, instalment, instalmentTotal } = item.projection ?? {};
          const position =
            instalment && instalmentTotal ? ` (${instalment} of ${instalmentTotal})` : "";
          // Tasks anchor on their due date, not a start — read the time from whichever the item has.
          const anchor = itemAnchorDate(item);
          const time = !item.allDay && anchor ? format(anchor, "HH:mm") : null;
          const title = identity ? `${identity.label}${position}` : item.summary || "(no title)";
          const accountCode = identity ? reference : null;
          // Tasks carry the same neutral slate checkbox identity as the grid chips and the activity feed.
          const glyph = identity
            ? { Icon: identity.Icon, tile: identity.tile }
            : isTask
              ? { Icon: CheckSquare, tile: "bg-slate-100 text-slate-700" }
              : null;
          const Icon = glyph?.Icon;

          return (
            <li key={item.occurrenceKey ?? item.uid}>
              <button
                type="button"
                onClick={() => onSelectItem?.(item)}
                className={cn(
                  "flex w-full gap-3 px-4 py-2.5 text-left hover:bg-zinc-50",
                  item.ownerColorClass && `border-l-4 ${item.ownerColorClass}`,
                )}
              >
                <span className="w-24 shrink-0 pt-0.5 text-xs tabular-nums text-zinc-500">
                  {dayLabel(itemAnchorDate(item))}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                    {Icon && (
                      <span
                        className={cn(
                          "flex size-5 shrink-0 items-center justify-center rounded",
                          glyph.tile,
                        )}
                      >
                        <Icon className="size-3" />
                      </span>
                    )}
                    {time && (
                      <span className="shrink-0 text-xs tabular-nums text-zinc-500">{time}</span>
                    )}
                    <span className="truncate">{title}</span>
                  </div>
                  {accountCode && (
                    <div className="mt-0.5 truncate text-xs text-zinc-500">{accountCode}</div>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AgendaList;
